import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Offcanvas } from 'react-bootstrap';
import './flowHistoryPanel.css';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { fetchFlowHistoryByRecord, startDownloadFlowHistoryExcel } from '../../../app/actions/flowHistoryActions';
import { startLoadingFlowEngineCatalogs } from '../../../app/actions/flowEngineActions';
import { startLoadingCatalogRoles } from '../../../app/actions/catalogRoleActions';
import {
  buildHistoryEventDiffs,
  findLastModificationByRole,
  formatDateTime,
  mergeRoleCatalogs,
  resolveBandejaTitle,
  resolveHistoryActorLine,
  resolveHistoryAssignmentLine,
  resolveHistoryEventTitle,
  resolveHistoryEventType,
  hasRecordMotivo,
  resolveRecordMotivo,
  resolveEventTimestamp,
  sortHistoryEventsDesc
} from '../../../app/helpers/flowHistoryDisplayHelper';
import {
  buildCatalogLookups,
  buildUsersByIdFromEvents
} from '../../../app/helpers/flowEditPageHelper';
import { resolveFlowStateSemaphore, getSemaphoreTitle } from '../../../app/helpers/flowStateSemaphoreHelper';
import { annotateHistoryEventsWithPhase, resolveCasePhase } from '../../../app/helpers/flowCasePhaseHelper';
import { isFlowHistoryExcelExportEnabled } from '../../../app/helpers/flowEngineConfigHelper';
import FlowStateSemaphore from './FlowStateSemaphore';
import FlowCasePhaseBadge from './FlowCasePhaseBadge';
import FlowHistoryAnalytics from './FlowHistoryAnalytics';

const FlowHistoryDiffList = ({ diffs }) => {
  if (!diffs?.length) {
    return null;
  }

  return (
    <ul className="list-unstyled small mb-0 mt-2 flow-history-diff-list">
      {diffs.map((entry) => (
        <li key={entry.field} className="mb-2">
          <strong>{entry.label}:</strong>{' '}
          <span className="flow-history-diff-pair">
            <span className="flow-history-diff-prev">{String(entry.previous)}</span>
            <span className="flow-history-diff-arrow" aria-hidden="true">→</span>
            <span className="flow-history-diff-next">{String(entry.next)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
};

FlowHistoryDiffList.propTypes = {
  diffs: PropTypes.array
};

const FlowRecordHistoryModal = ({
  show,
  onHide,
  record,
  moduleId,
  moduleProcess = 'technology',
  selectedRole = null,
  subjectKind = ''
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const flowEngine = useSelector((state) => state.flowEngine);
  const loginRoles = useSelector((state) => state.userRole?.roleLogin?.roles || []);
  const catalogRoles = useSelector((state) => state.catalogRole?.catalogRoles || []);
  const roleFromStore = useSelector((state) => state.userRole?.roleLogin?.roleSpecialistSelected);
  const auth = useSelector((state) => state.auth);
  const catalog = useSelector((state) => state.catalog);
  const catalogCiiu = useSelector((state) => state.catalogCiiu);
  const catalogSection = useSelector((state) => state.catalogSection);
  const catalogPhase = useSelector((state) => state.catalogPhase);
  const locationCountry = useSelector((state) => state.locationCountry);
  const configuration = useSelector((state) => state.configuration);
  const activeRole = selectedRole || roleFromStore;
  const excelExportEnabled = isFlowHistoryExcelExportEnabled(configuration);

  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const allRoles = useMemo(
    () => mergeRoleCatalogs(loginRoles, catalogRoles, activeRole ? [activeRole] : []),
    [loginRoles, catalogRoles, activeRole]
  );

  useEffect(() => {
    if (!show || !record?.id || !moduleId) {
      return;
    }

    if (!flowEngine.loaded && !flowEngine.loading) {
      dispatch(startLoadingFlowEngineCatalogs());
    }

    if (!catalogRoles.length) {
      dispatch(startLoadingCatalogRoles());
    }

    let cancelled = false;

    const loadHistory = async () => {
      setLoading(true);
      setError('');
      try {
        const rows = await fetchFlowHistoryByRecord(moduleId, record.id, subjectKind || record.subject_kind);
        if (!cancelled) {
          setEvents(rows);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.msg || t('general.flow-engine.history.load-error', 'No se pudo cargar el historial.'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadHistory();

    return () => {
      cancelled = true;
    };
  }, [show, record?.id, record?.subject_kind, moduleId, subjectKind, dispatch, flowEngine.loaded, flowEngine.loading, catalogRoles.length, t]);

  const bandejaTitle = useMemo(
    () => (record ? resolveBandejaTitle(record, allRoles, t, activeRole, flowEngine) : ''),
    [record, allRoles, t, activeRole, flowEngine]
  );

  const recordMotivo = useMemo(
    () => (record ? resolveRecordMotivo(record, flowEngine, t) : ''),
    [record, flowEngine, t]
  );
  const showRecordMotivo = useMemo(
    () => (record ? hasRecordMotivo(record, flowEngine) : false),
    [record, flowEngine]
  );

  const lastModification = useMemo(
    () => findLastModificationByRole(events, record?.current_role_catalog_id),
    [events, record?.current_role_catalog_id]
  );

  const usersById = useMemo(
    () => buildUsersByIdFromEvents(events, auth?.user),
    [events, auth?.user]
  );

  const catalogLookups = useMemo(
    () => buildCatalogLookups({ catalog, catalogCiiu, catalogSection, catalogPhase, locationCountry }),
    [catalog, catalogCiiu, catalogSection, catalogPhase, locationCountry]
  );

  const lastModificationDiffs = useMemo(() => {
    if (!lastModification) {
      return [];
    }
    return buildHistoryEventDiffs({
      event: lastModification,
      moduleProcess,
      flowEngine,
      roles: allRoles,
      t,
      usersById,
      catalogLookups,
      hideReasonDetail: false
    });
  }, [lastModification, flowEngine, allRoles, t, usersById, catalogLookups, moduleProcess]);

  const roleNameById = useMemo(() => {
    const map = {};
    (allRoles || []).forEach((role) => {
      if (role?.id) {
        map[Number(role.id)] = role.name;
      }
    });
    return map;
  }, [allRoles]);

  const publishedStateIds = useMemo(
    () => (flowEngine.states || [])
      .filter((state) => ['publicado', 'published'].includes(String(state.name || '').trim().toLowerCase()))
      .map((state) => Number(state.id)),
    [flowEngine.states]
  );

  const annotatedEvents = useMemo(
    () => annotateHistoryEventsWithPhase(events, {
      process: moduleProcess,
      roleNameById,
      publishedStateIds
    }),
    [events, moduleProcess, roleNameById, publishedStateIds]
  );

  const timelineEvents = useMemo(
    () => sortHistoryEventsDesc(annotatedEvents),
    [annotatedEvents]
  );

  const currentCasePhase = useMemo(() => {
    const recordRole = allRoles.find(
      (role) => Number(role.id) === Number(record?.current_role_catalog_id)
    );
    return resolveCasePhase(record, {
      roleName: recordRole?.name,
      process: moduleProcess
    });
  }, [allRoles, moduleProcess, record]);

  const semaphore = resolveFlowStateSemaphore(record?.state_catalog_id, flowEngine.states);

  const handleExportExcel = async () => {
    if (!record?.id || !moduleId || exporting) {
      return;
    }
    setExporting(true);
    try {
      await startDownloadFlowHistoryExcel(moduleId, record.id, record.code || record.case_code || '');
    } finally {
      setExporting(false);
    }
  };

  if (!record) {
    return null;
  }

  const caseCode = record.code || record.case_code || '';

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      scroll={false}
      className="flow-history-offcanvas"
      style={{ '--bs-offcanvas-width': '50vw', width: '50vw' }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title className="d-flex align-items-center gap-2 flex-wrap">
          <FlowStateSemaphore stateCatalogId={record?.state_catalog_id} onClick={() => {}} />
          <span>{getSemaphoreTitle(semaphore, t)}</span>
          <FlowCasePhaseBadge phase={currentCasePhase} />
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {loading && (
          <p className="text-muted">{t('general.loading', 'Cargando...')}</p>
        )}
        {error && (
          <div className="alert alert-warning">{error}</div>
        )}

        {!loading && !error && (
          <>
            <div className="mb-3 p-3 bg-light rounded border">
              <div className="mb-2">
                <strong>{bandejaTitle}</strong>
              </div>
              <div className={showRecordMotivo ? 'mb-2' : 'mb-0'}>
                <strong>{t('flow.record.case-number', 'Número de caso')}:</strong> {record.id}
                {caseCode ? (
                  <>
                    {' · '}
                    <strong>{t('flow.record.case-code', 'Código')}:</strong> {caseCode}
                  </>
                ) : null}
              </div>
              {showRecordMotivo && (
                <div className="mb-0">
                  <strong>{t('flow.motivo.label', 'Motivo')}:</strong> {recordMotivo}
                </div>
              )}
            </div>

            {lastModification && (
              <div className="mb-3 p-3 border border-info rounded bg-white">
                <h6 className="text-info mb-2">
                  {t('general.flow-engine.history.last-changes', 'Últimos cambios del perfil')}
                </h6>
                <div className="small text-muted mb-2">
                  {resolveHistoryActorLine(lastModification, allRoles, t, usersById)}
                </div>
                <FlowHistoryDiffList diffs={lastModificationDiffs} />
              </div>
            )}

            <h6 className="text-uppercase text-muted small mb-3">
              {t('general.flow-engine.history.process-title', 'Historial de proceso')}
              {timelineEvents.length > 0 && (
                <span className="ms-2 badge text-bg-secondary">{timelineEvents.length}</span>
              )}
            </h6>

            {timelineEvents.length === 0 ? (
              <p className="text-muted small">{t('flow.history.no-process-events', 'Sin eventos del motor de flujos.')}</p>
            ) : (
              <div className="flow-history-timeline mb-2">
                {timelineEvents.map((event) => {
                  const title = resolveHistoryEventTitle(event, t);
                  const typeLabel = resolveHistoryEventType(event, t);
                  const actorLine = resolveHistoryActorLine(event, allRoles, t, usersById);
                  const assignmentLine = resolveHistoryAssignmentLine(event, t, usersById);
                  const diffs = buildHistoryEventDiffs({
                    event,
                    moduleProcess,
                    flowEngine,
                    roles: allRoles,
                    t,
                    usersById,
                    catalogLookups,
                    hideReasonDetail: true
                  });

                  return (
                    <div key={event.id} className={`flow-history-timeline-item mb-3 p-3 border rounded bg-white${event.case_phase ? ` is-phase-${event.case_phase}` : ''}`}>
                      <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                            <strong>{title}</strong>
                            <span className="badge rounded-pill text-bg-light border">{typeLabel}</span>
                            {event.case_phase && <FlowCasePhaseBadge phase={event.case_phase} />}
                          </div>
                          <div className="small text-muted">{actorLine}</div>
                          {assignmentLine ? (
                            <div className="small text-muted">{assignmentLine}</div>
                          ) : null}
                        </div>
                        <small className="text-muted text-nowrap">
                          {formatDateTime(resolveEventTimestamp(event))}
                        </small>
                      </div>

                      {event.reason_detail && (
                        <div className="small mt-2 p-2 bg-light rounded border-start border-3 border-primary-subtle">
                          <span className="text-muted">{t('flow.motivo.label', 'Motivo')}:</span>{' '}
                          {event.reason_detail}
                        </div>
                      )}

                      <FlowHistoryDiffList diffs={diffs} />
                    </div>
                  );
                })}
              </div>
            )}

            {!lastModification && timelineEvents.length === 0 && (
              <p className="text-muted small mb-0">
                {t('flow.history.no-modifications', 'No existen modificaciones en el registro.')}
              </p>
            )}

            <FlowHistoryAnalytics
              events={annotatedEvents}
              roles={allRoles}
              states={flowEngine.states}
            />
          </>
        )}
      </Offcanvas.Body>
      <div className="border-top p-3 d-flex justify-content-between align-items-center gap-2">
        {excelExportEnabled ? (
          <button
            type="button"
            className="btn btn-outline-success"
            onClick={handleExportExcel}
            disabled={exporting || loading || !timelineEvents.length}
          >
            <i className="fa fa-file-excel-o me-2" aria-hidden="true" />
            {exporting
              ? t('general.flow-engine.history.exporting', 'Exportando...')
              : t('general.flow-engine.history.export-excel', 'Exportar historial a Excel')}
          </button>
        ) : (
          <span />
        )}
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          {t('buttons.close', 'Cerrar')}
        </button>
      </div>
    </Offcanvas>
  );
};

FlowRecordHistoryModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  record: PropTypes.object,
  moduleId: PropTypes.number.isRequired,
  moduleProcess: PropTypes.string,
  selectedRole: PropTypes.object,
  subjectKind: PropTypes.string
};

export default FlowRecordHistoryModal;
